import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import flags from './flags';
import { ICON_SIZE } from '../constants';
import { sizes } from '../breakpoints';
import { getPx } from '../helpers/common';
import { warn } from '../helpers/console';
import { useDeviceType, useMediaBreakpoints } from '../hooks';
import { size } from '../style/size';
const VERSION = 'v3';
const SPRITE_URL = (screen, scale) => `https://tourlane-ui.imgix.net/flags/${VERSION}/sprites/${screen}/sprite@${scale}x.png`;
const ICON_URL = (country, screen, scale) => `https://tourlane-ui.imgix.net/flags/${VERSION}/icons/${screen}/${country}@${scale}x.png`;
const SIZE_RATIO = 0.75;
const ICON_FLAG_HEIGHT = Object.keys(ICON_SIZE).reduce((acc, size) => ({
  ...acc,
  [size]: ICON_SIZE[size] * SIZE_RATIO
}), {});
const StyledSpriteImage = styled.span`
  display: block;
  background-image: ${({
  screen,
  scale
}) => `url('${SPRITE_URL(screen, scale)}')`};
  background-size: ${({
  screen
}) => `${getPx(flags.properties[screen].width)} ${getPx(flags.properties[screen].height)}`};
  background-repeat: no-repeat;
  width: ${({
  width
}) => getPx(width)};
  height: ${({
  height
}) => getPx(height)};
  ${({
  rounded
}) => rounded && `
    overflow: hidden;
    border-radius: 4px;
  `}
`;
const StyledIconImage = styled.span`
  display: block;
  background-image: ${({
  country,
  screen,
  scale
}) => `url('${ICON_URL(country, screen, scale)}')`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: ${({
  size
}) => `${getPx(size)} ${getPx(size)}`};
  width: ${({
  width
}) => getPx(width)};
  height: ${({
  height
}) => getPx(height)};
  ${({
  rounded
}) => rounded && `
    overflow: hidden;
    border-radius: 4px;
  `}
`;
const StyledWrapper = styled.span`
  display: ${({
  $flex
}) => $flex ? 'inline-flex' : 'inline-block'};
  align-items: center;
  justify-content: center;
  ${size(ICON_SIZE)}
`;
export const useIconFlagSpriteUrl = () => {
  const {
    pixelRatio
  } = useDeviceType();
  const screen = useMediaBreakpoints(sizes);
  const scale = useMemo(() => Math.max(Math.min(Math.ceil(pixelRatio), 4), 1), [pixelRatio]);
  return useMemo(() => SPRITE_URL(screen, scale), [screen, scale]);
};
const IconFlag = ({
  rounded = false,
  flex = true,
  asIcon = false,
  country,
  ...props
}) => {
  const {
    pixelRatio
  } = useDeviceType();
  const screen = useMediaBreakpoints(sizes);
  const size = useMediaBreakpoints(ICON_SIZE);
  const height = useMediaBreakpoints(ICON_FLAG_HEIGHT);
  const scale = useMemo(() => Math.max(Math.min(Math.ceil(pixelRatio), 4), 1), [pixelRatio]);
  const coordinates = flags.coordinates[country];
  if (!coordinates) {
    warn('IconFlag', `Country ${country} is not supported!`);
    return null;
  }
  return /*#__PURE__*/React.createElement(StyledWrapper, _extends({
    $flex: flex
  }, props), asIcon ? /*#__PURE__*/React.createElement(StyledIconImage, {
    screen: screen,
    scale: scale,
    size: size,
    width: size,
    height: height,
    rounded: rounded,
    country: country
  }) : /*#__PURE__*/React.createElement(StyledSpriteImage, {
    screen: screen,
    scale: scale,
    rounded: rounded,
    width: size,
    height: height,
    style: {
      backgroundPosition: `
              -${getPx(coordinates.x * size)}
              -${getPx(coordinates.y * size + size * (1 - SIZE_RATIO) / 2)}
            `
    }
  }));
};
export default IconFlag;